import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import ApexCharts from 'apexcharts';
import { Empty, Typography } from 'antd';
import { InfoPoint } from '../BondsPlayground/ModelGuide';

type YieldInterpolation = {
  lower_tenor_months?: number;
  upper_tenor_months?: number;
  lower_tenor_years?: number;
  upper_tenor_years?: number;
  lower_bid_yield: number | null;
  lower_ask_yield: number | null;
  upper_bid_yield: number | null;
  upper_ask_yield: number | null;
  upper_weight: number;
  lower_source_timestamp: string | null;
  upper_source_timestamp: string | null;
};

export type IssuedBondPricePoint = {
  timestamp: string;
  clean_bid: number | null;
  clean_ask: number | null;
  dirty_bid: number | null;
  dirty_ask: number | null;
  interpolated_bid_yield: number | null;
  interpolated_ask_yield: number | null;
  accrued_interest: number | null;
  accrued_days: number | null;
  previous_coupon_timestamp: string | null;
  next_coupon_timestamp: string | null;
  residual_maturity_years: number | null;
  yield_interpolation: YieldInterpolation | null;
};

type Props = {
  points: IssuedBondPricePoint[];
  chartIdPrefix?: string;
  groupId?: string;
};

type ChartPoint = {
  x: string;
  y: number | null;
};

type ChartSeries = {
  name: string;
  data: ChartPoint[];
};

type ChartValue = number | null | undefined;

type TooltipContext = {
  dataPointIndex?: number;
  seriesIndex?: number;
  w?: {
    globals?: {
      seriesX?: number[][];
    };
  };
};

type SynchronizedPanelProps = {
  chartId: string;
  groupId: string;
  colors: string[];
  help: React.ReactNode;
  helpTitle: string;
  label: string;
  series: ChartSeries[];
  strokeDashArray?: number[];
  tooltipCustom?: (context: TooltipContext) => string;
  valueFormatter: (value: ChartValue) => string;
  showLegend?: boolean;
  showReset?: boolean;
  showTimeline?: boolean;
};

type YieldConstituentsPanelProps = {
  chartId: string;
  groupId: string;
  series: ChartSeries[];
  tooltipCustom: (context: TooltipContext) => string;
};

type MountedLineChartProps = {
  height: number;
  options: Record<string, unknown>;
  series: ChartSeries[];
};

export type IssuedBondDerivedChartSeries = {
  priceSeries: ChartSeries[];
  yieldSeries: ChartSeries[];
  constituentSeries: ChartSeries[];
  accruedInterestSeries: ChartSeries[];
  maturitySeries: ChartSeries[];
  pointsByTimestamp: Map<number, IssuedBondPricePoint>;
};

const CHART_GROUP = 'issued-bond-evolution';
const Y_AXIS_LABEL_WIDTH = 68;
const BID_COLOR = '#1677ff';
const ASK_COLOR = '#fa8c16';
const YIELD_TENOR_MONTHS = [120, 84, 60, 36, 24, 12, 6] as const;
const YIELD_TENOR_COLORS = [
  '#cf1322',
  '#722ed1',
  '#13c2c2',
  '#52c41a',
  '#1677ff',
  '#fa8c16',
  '#8c8c8c',
];
const PRICE_COLORS = [BID_COLOR, ASK_COLOR];
const ACCRUED_INTEREST_COLORS = ['#722ed1'];
const MATURITY_COLORS = ['#52c41a'];
const YIELD_CONSTITUENT_COLORS = YIELD_TENOR_COLORS.flatMap(color => [
  color,
  color,
]);
const YIELD_CONSTITUENT_DASH_ARRAY = YIELD_TENOR_MONTHS.flatMap(() => [6, 0]);
const derivedSeriesCache = new WeakMap<
  IssuedBondPricePoint[],
  IssuedBondDerivedChartSeries
>();

const tenorLabel = (tenorMonths: number) =>
  tenorMonths < 12 ? `${tenorMonths}M` : `${tenorMonths / 12}Y`;

const lowerTenorMonths = (interpolation: YieldInterpolation) =>
  Number(
    interpolation.lower_tenor_months ??
      Number(interpolation.lower_tenor_years) * 12,
  );

const upperTenorMonths = (interpolation: YieldInterpolation) =>
  Number(
    interpolation.upper_tenor_months ??
      Number(interpolation.upper_tenor_years) * 12,
  );

const isFiniteNumber = (value: ChartValue): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const priceFormatter = (value: ChartValue) =>
  isFiniteNumber(value)
    ? value.toLocaleString(undefined, { maximumFractionDigits: 6 })
    : '';
const yieldFormatter = (value: ChartValue) =>
  isFiniteNumber(value) ? `${value.toFixed(4)}%` : '';
const accruedInterestFormatter = (value: ChartValue) =>
  isFiniteNumber(value) ? `${value.toFixed(6)}%` : '';
const residualMaturityFormatter = (value: ChartValue) =>
  isFiniteNumber(value) ? `${value.toFixed(4)}y` : '';

const tooltipNumber = (
  value: ChartValue,
  maximumFractionDigits: number,
  suffix = '',
) =>
  isFiniteNumber(value)
    ? `${value.toLocaleString(undefined, { maximumFractionDigits })}${suffix}`
    : 'Not available';

const tooltipTimestamp = (value: string | null | undefined) => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? 'Not available'
    : parsed.toLocaleString();
};

const tooltipIndex = (context: TooltipContext, length: number) => {
  const index = context.dataPointIndex;
  return typeof index === 'number' && index >= 0 && index < length
    ? index
    : undefined;
};

const timestampKey = (timestamp: string) => new Date(timestamp).getTime();

export const issuedBondDerivedChartSeries = (
  points: IssuedBondPricePoint[],
): IssuedBondDerivedChartSeries => {
  const cached = derivedSeriesCache.get(points);
  if (cached) return cached;

  const priceSeries: ChartSeries[] = [
    { name: 'Clean bid', data: [] },
    { name: 'Clean ask', data: [] },
  ];
  const yieldSeries: ChartSeries[] = [
    { name: 'Interpolated bid yield', data: [] },
    { name: 'Interpolated ask yield', data: [] },
  ];
  const accruedInterestSeries: ChartSeries[] = [
    { name: 'Accrued interest', data: [] },
  ];
  const maturitySeries: ChartSeries[] = [
    { name: 'Residual maturity', data: [] },
  ];
  const constituentSeries: ChartSeries[] = YIELD_TENOR_MONTHS.flatMap(
    tenorMonths => [
      { name: `${tenorLabel(tenorMonths)} bid`, data: [] },
      { name: `${tenorLabel(tenorMonths)} ask`, data: [] },
    ],
  );
  const constituentIndexes = new Map<number, number>();
  YIELD_TENOR_MONTHS.forEach((tenorMonths, index) =>
    constituentIndexes.set(tenorMonths, index * 2),
  );
  const pointsByTimestamp = new Map<number, IssuedBondPricePoint>();

  points.forEach(point => {
    priceSeries[0].data.push({ x: point.timestamp, y: point.clean_bid });
    priceSeries[1].data.push({ x: point.timestamp, y: point.clean_ask });
    yieldSeries[0].data.push({
      x: point.timestamp,
      y: point.interpolated_bid_yield,
    });
    yieldSeries[1].data.push({
      x: point.timestamp,
      y: point.interpolated_ask_yield,
    });
    accruedInterestSeries[0].data.push({
      x: point.timestamp,
      y: point.accrued_interest,
    });
    maturitySeries[0].data.push({
      x: point.timestamp,
      y: point.residual_maturity_years,
    });
    pointsByTimestamp.set(timestampKey(point.timestamp), point);

    const interpolation = point.yield_interpolation;
    if (!interpolation) return;
    const lowerMonths = lowerTenorMonths(interpolation);
    const upperMonths = upperTenorMonths(interpolation);
    const activeTenors =
      lowerMonths === upperMonths ? [lowerMonths] : [lowerMonths, upperMonths];
    activeTenors.forEach(tenorMonths => {
      const bidIndex = constituentIndexes.get(tenorMonths);
      if (bidIndex === undefined) return;
      const lower = tenorMonths === lowerMonths;
      constituentSeries[bidIndex].data.push({
        x: point.timestamp,
        y: lower
          ? interpolation.lower_bid_yield
          : interpolation.upper_bid_yield,
      });
      constituentSeries[bidIndex + 1].data.push({
        x: point.timestamp,
        y: lower
          ? interpolation.lower_ask_yield
          : interpolation.upper_ask_yield,
      });
    });
  });

  const derived = {
    priceSeries,
    yieldSeries,
    constituentSeries,
    accruedInterestSeries,
    maturitySeries,
    pointsByTimestamp,
  };
  derivedSeriesCache.set(points, derived);
  return derived;
};

const MountedLineChart: React.FC<MountedLineChartProps> = ({
  height,
  options,
  series,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ApexCharts>();
  const renderRef = useRef<Promise<void>>();
  const mountedSeriesRef = useRef<ChartSeries[]>(series);
  const initialSeriesRef = useRef<ChartSeries[]>(series);
  const updateVersionRef = useRef(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const chart = new ApexCharts(containerRef.current, {
      ...options,
      chart: {
        ...((options.chart as Record<string, unknown>) || {}),
        height,
        type: 'line',
      },
      series: initialSeriesRef.current,
    });
    chartRef.current = chart;
    renderRef.current = chart.render();
    return () => {
      updateVersionRef.current += 1;
      chartRef.current = undefined;
      chart.destroy();
    };
  }, [height, options]);

  useEffect(() => {
    if (mountedSeriesRef.current === series) return;
    mountedSeriesRef.current = series;
    const updateVersion = ++updateVersionRef.current;
    void renderRef.current?.then(() => {
      if (chartRef.current && updateVersionRef.current === updateVersion) {
        void chartRef.current.updateSeries(
          series as ApexAxisChartSeries,
          false,
        );
      }
    });
  }, [series]);

  return <div ref={containerRef} />;
};

const SynchronizedPanel: React.FC<SynchronizedPanelProps> = ({
  chartId,
  groupId,
  colors,
  help,
  helpTitle,
  label,
  series,
  strokeDashArray,
  tooltipCustom,
  valueFormatter,
  showLegend = false,
  showReset = false,
  showTimeline = false,
}) => {
  const options: any = useMemo(
    () => ({
      chart: {
        id: chartId,
        group: groupId,
        animations: { enabled: false },
        parentHeightOffset: 0,
        selection: { enabled: true, type: 'x' },
        toolbar: {
          show: showReset,
          autoSelected: 'zoom',
          tools: {
            download: false,
            selection: false,
            zoom: true,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: showReset,
          },
        },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
        },
      },
      colors,
      dataLabels: { enabled: false },
      grid: {
        borderColor: '#e8e8e8',
        padding: {
          top: showReset ? -4 : 4,
          right: 12,
          bottom: showTimeline ? 0 : -8,
          left: 6,
        },
      },
      legend: {
        show: showLegend,
        fontSize: '11px',
        horizontalAlign: 'left',
        offsetX: Y_AXIS_LABEL_WIDTH,
        markers: { width: 9, height: 9, radius: 9 },
      },
      markers: { size: 0 },
      stroke: {
        curve: 'straight',
        width: 2,
        ...(strokeDashArray ? { dashArray: strokeDashArray } : {}),
      },
      tooltip: {
        shared: true,
        intersect: false,
        hideEmptySeries: true,
        x: { format: 'dd MMM yyyy HH:mm' },
        ...(tooltipCustom
          ? { custom: tooltipCustom }
          : { y: { formatter: valueFormatter } }),
      },
      xaxis: {
        type: 'datetime',
        crosshairs: { show: true },
        labels: {
          show: showTimeline,
          datetimeUTC: false,
        },
        axisBorder: { show: showTimeline },
        axisTicks: { show: showTimeline },
        tooltip: { enabled: false },
        ...(showTimeline ? { title: { text: 'Valuation timestamp' } } : {}),
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          minWidth: Y_AXIS_LABEL_WIDTH,
          maxWidth: Y_AXIS_LABEL_WIDTH,
          formatter: valueFormatter,
        },
      },
    }),
    [
      chartId,
      colors,
      groupId,
      showLegend,
      showReset,
      showTimeline,
      strokeDashArray,
      tooltipCustom,
      valueFormatter,
    ],
  );

  return (
    <div className="bonds-playground__synced-chart-panel">
      <div className="bonds-playground__chart-panel-heading">
        <Typography.Text strong>{label}</Typography.Text>
        <InfoPoint title={helpTitle}>{help}</InfoPoint>
      </div>
      <MountedLineChart height={190} options={options} series={series} />
    </div>
  );
};

const YieldConstituentsPanel: React.FC<YieldConstituentsPanelProps> = ({
  chartId,
  groupId,
  series,
  tooltipCustom,
}) => {
  const options: any = useMemo(
    () => ({
      chart: {
        id: chartId,
        group: groupId,
        animations: { enabled: false },
        parentHeightOffset: 0,
        selection: { enabled: true, type: 'x' },
        toolbar: {
          show: false,
          autoSelected: 'zoom',
          tools: {
            download: false,
            selection: false,
            zoom: true,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
        },
      },
      colors: YIELD_CONSTITUENT_COLORS,
      dataLabels: { enabled: false },
      grid: {
        borderColor: '#e8e8e8',
        padding: { top: 4, right: 12, bottom: -8, left: 6 },
      },
      legend: {
        show: true,
        fontSize: '11px',
        horizontalAlign: 'left',
        offsetX: Y_AXIS_LABEL_WIDTH,
        markers: { width: 9, height: 9, radius: 9 },
      },
      markers: { size: 0 },
      stroke: {
        curve: 'straight',
        width: 2,
        dashArray: YIELD_CONSTITUENT_DASH_ARRAY,
      },
      tooltip: {
        shared: true,
        intersect: false,
        hideEmptySeries: true,
        custom: tooltipCustom,
      },
      xaxis: {
        type: 'datetime',
        crosshairs: { show: true },
        labels: { show: false, datetimeUTC: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          minWidth: Y_AXIS_LABEL_WIDTH,
          maxWidth: Y_AXIS_LABEL_WIDTH,
          formatter: yieldFormatter,
        },
      },
    }),
    [chartId, groupId, tooltipCustom],
  );

  return (
    <div className="bonds-playground__synced-chart-panel bonds-playground__synced-chart-panel--constituents">
      <div className="bonds-playground__chart-panel-heading">
        <Typography.Text strong>
          Interpolation constituent yields
        </Typography.Text>
        <InfoPoint title="Interpolation constituents">
          Only the system-yield tenor or tenors bracketing residual maturity are
          active. Each tenor keeps one color; its dashed line is bid yield and
          its solid line is ask yield. At an exact maintained tenor, only that
          node is used.
        </InfoPoint>
      </div>
      <MountedLineChart height={260} options={options} series={series} />
    </div>
  );
};

const IssuedBondPriceChart: React.FC<Props> = ({
  points,
  chartIdPrefix = 'issued-bond',
  groupId = CHART_GROUP,
}) => {
  const derived = useMemo(() => issuedBondDerivedChartSeries(points), [points]);
  const pointsRef = useRef(points);
  const derivedRef = useRef(derived);
  pointsRef.current = points;
  derivedRef.current = derived;

  const priceTooltip = useCallback((context: TooltipContext) => {
    const currentPoints = pointsRef.current;
    const index = tooltipIndex(context, currentPoints.length);
    if (index === undefined) return '';
    const point = currentPoints[index];

    return `
        <div class="bonds-playground__apex-tooltip">
          <div class="bonds-playground__apex-tooltip-title">${tooltipTimestamp(
            point.timestamp,
          )}</div>
          <div><strong>Clean bid:</strong> ${tooltipNumber(
            point.clean_bid,
            6,
          )}</div>
          <div><strong>Clean ask:</strong> ${tooltipNumber(
            point.clean_ask,
            6,
          )}</div>
          <div><strong>Dirty bid:</strong> ${tooltipNumber(
            point.dirty_bid,
            6,
          )}</div>
          <div><strong>Dirty ask:</strong> ${tooltipNumber(
            point.dirty_ask,
            6,
          )}</div>
          <div><strong>Accrued interest:</strong> ${tooltipNumber(
            point.accrued_interest,
            6,
            '%',
          )}</div>
        </div>`;
  }, []);
  const accruedInterestTooltip = useCallback((context: TooltipContext) => {
    const currentPoints = pointsRef.current;
    const index = tooltipIndex(context, currentPoints.length);
    if (index === undefined) return '';
    const point = currentPoints[index];

    return `
        <div class="bonds-playground__apex-tooltip">
          <div class="bonds-playground__apex-tooltip-title">${tooltipTimestamp(
            point.timestamp,
          )}</div>
          <div><strong>Accrued interest:</strong> ${tooltipNumber(
            point.accrued_interest,
            6,
            '%',
          )}</div>
          <div><strong>Accrued days:</strong> ${tooltipNumber(
            point.accrued_days,
            0,
          )}</div>
          <div><strong>Previous coupon:</strong> ${tooltipTimestamp(
            point.previous_coupon_timestamp,
          )}</div>
          <div><strong>Next coupon:</strong> ${tooltipTimestamp(
            point.next_coupon_timestamp,
          )}</div>
        </div>`;
  }, []);
  const constituentTooltip = useCallback((context: TooltipContext) => {
    const seriesIndex = context.seriesIndex;
    const dataPointIndex = context.dataPointIndex;
    const timestamp =
      typeof seriesIndex === 'number' && typeof dataPointIndex === 'number'
        ? context.w?.globals?.seriesX?.[seriesIndex]?.[dataPointIndex]
        : undefined;
    const point =
      typeof timestamp === 'number'
        ? derivedRef.current.pointsByTimestamp.get(timestamp)
        : undefined;
    const interpolation = point?.yield_interpolation;
    if (!point || !interpolation) return '';
    const lowerMonths = lowerTenorMonths(interpolation);
    const upperMonths = upperTenorMonths(interpolation);
    const hasTwoNodes = lowerMonths !== upperMonths;

    return `
      <div class="bonds-playground__apex-tooltip">
        <div class="bonds-playground__apex-tooltip-title">${tooltipTimestamp(
          point.timestamp,
        )}</div>
        <div><strong>${tenorLabel(lowerMonths)} bid:</strong> ${tooltipNumber(
          interpolation.lower_bid_yield,
          4,
          '%',
        )}</div>
        <div><strong>${tenorLabel(lowerMonths)} ask:</strong> ${tooltipNumber(
          interpolation.lower_ask_yield,
          4,
          '%',
        )}</div>
        <div><strong>Lower-node source:</strong> ${tooltipTimestamp(
          interpolation.lower_source_timestamp,
        )}</div>
        ${
          hasTwoNodes
            ? `<div class="bonds-playground__apex-tooltip-separator"></div>
               <div><strong>${tenorLabel(upperMonths)} bid:</strong> ${tooltipNumber(
                 interpolation.upper_bid_yield,
                 4,
                 '%',
               )}</div>
               <div><strong>${tenorLabel(upperMonths)} ask:</strong> ${tooltipNumber(
                 interpolation.upper_ask_yield,
                 4,
                 '%',
               )}</div>
               <div><strong>Upper-node source:</strong> ${tooltipTimestamp(
                 interpolation.upper_source_timestamp,
               )}</div>
               <div><strong>Upper-node weight:</strong> ${tooltipNumber(
                 interpolation.upper_weight * 100,
                 2,
                 '%',
               )}</div>`
            : ''
        }
      </div>`;
  }, []);

  return (
    <div className="bonds-playground__issued-price-chart">
      <Typography.Title level={5}>Issued bond evolution</Typography.Title>
      <Typography.Paragraph type="secondary">
        Clean bid/ask prices, their continuously interpolated yields, the active
        system-yield constituents, accrued interest, and residual maturity share
        one synchronized timeline. Hover any panel to inspect the same timestamp
        across all five; click and drag to zoom, then use the reset control on
        the clean-price panel.
      </Typography.Paragraph>
      {points.length ? (
        <div className="bonds-playground__synced-charts">
          <SynchronizedPanel
            chartId={`${chartIdPrefix}-clean-price`}
            colors={PRICE_COLORS}
            groupId={groupId}
            help="The platform-facing quote excludes accrued interest. Hover to see both clean quotes, their dirty settlement values, and the accrued-interest bridge between them."
            helpTitle="Clean bid and ask"
            label="Clean price"
            series={derived.priceSeries}
            showLegend
            showReset
            tooltipCustom={priceTooltip}
            valueFormatter={priceFormatter}
          />
          <SynchronizedPanel
            chartId={`${chartIdPrefix}-interpolated-yields`}
            colors={PRICE_COLORS}
            groupId={groupId}
            help="Bid and ask yields are interpolated independently from their corresponding maintained curve nodes. Each moving yield discounts all remaining cash flows on its side of the market."
            helpTitle="Interpolated bid and ask yields"
            label="Interpolated yield"
            series={derived.yieldSeries}
            showLegend
            valueFormatter={yieldFormatter}
          />
          <YieldConstituentsPanel
            chartId={`${chartIdPrefix}-yield-constituents`}
            groupId={groupId}
            series={derived.constituentSeries}
            tooltipCustom={constituentTooltip}
          />
          <SynchronizedPanel
            chartId={`${chartIdPrefix}-accrued-interest`}
            colors={ACCRUED_INTEREST_COLORS}
            groupId={groupId}
            help="Coupon interest accumulated since the previous coupon date, expressed as a percentage of face value. Hover for accrued days and the surrounding coupon timestamps."
            helpTitle="Accrued interest"
            label="Accrued interest"
            series={derived.accruedInterestSeries}
            tooltipCustom={accruedInterestTooltip}
            valueFormatter={accruedInterestFormatter}
          />
          <SynchronizedPanel
            chartId={`${chartIdPrefix}-residual-maturity`}
            colors={MATURITY_COLORS}
            groupId={groupId}
            help="Time remaining to final maturity, expressed continuously in semiannual coupon-period units. Current pricing-v2 series stop below 6M; legacy pricing-v1 series stop below 2Y."
            helpTitle="Residual maturity"
            label="Residual maturity"
            series={derived.maturitySeries}
            showTimeline
            valueFormatter={residualMaturityFormatter}
          />
        </div>
      ) : (
        <Empty description="No yield observations are available after issuance." />
      )}
    </div>
  );
};

export default React.memo(IssuedBondPriceChart);
